/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from 'react';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { Button, Tab, Tabs } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { useTranslation } from 'react-i18next';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import { Code, commonYNList } from 'models/common/CommonCode';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { SetupExcpParam, ItemList } from 'models/setup/excp/excp';
import { SetupProject } from 'models/setup/SetupProject';
import { findExcpByElmPrjId } from 'apis/setup/SetupSchedule';
import { SetupSchedule } from 'models/setup/SetupSchedule';
import { useLoading } from 'components/process/Loading';
import { useMessageBar } from 'hooks/useMessageBar';
import { getSetupItemList, getProjectItemCnt } from 'apis/setup/excp/Item';
import useSessionStore from 'stores/useSessionStore';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { hasRole } from 'utils/SessionUtil';
import _ from 'lodash';
import { tabs } from 'components/layouts/Tab';
import SetupItemList from './SetupItemList';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { findDisplayProject } from 'apis/setup/SetupProject';

/**
 * @description 하역장관리 > 품목정보 등록/조회
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 *  26.03.04    jihwan89      다른 페이지 MDI locationState의 영향을 받아 조회가 안되거나 변경되는 오류 수정
 *  26.05.06    jihwan89      하역장 대시보드에서 location 이동 시 상태별 조회 추가
 */

const SetupItemManagementPage = () => {
  const { t } = useTranslation();
  const { userId, deptCd, deptNm, elmPrjId, ensolYn } = useSessionStore();
  const { openMessageBar } = useMessageBar();
  const { openLoading } = useLoading();
  const locationState = useLocation();
  const [locationParam, setLocationParam] = useState({ ...locationState.state });
  const [locationStatChange, setLocationStatChange] = useState<boolean>(false);

  const [bildPlntCode, setBildPlntCode] = useState<Code[]>(); // 플랜트
  const [pakItemStatCode, setPakItemStatCode] = useState<Code[]>(); // 플랜트
  const [prjMasterData, setPrjMasterData] = useState<SetupProject[]>([]); //프로젝트
  const [prjExcpData, setPrjExcpData] = useState<SetupSchedule[]>([]); //협력사
  const [procCheckCode, setProcCheckCode] = useState<Code[]>([]); //진척점검
  const [rowData, setRowData] = useState<ItemList[]>([]); //그리드
  const [errors, setErrors] = useState<any>({});
  const [projectCnt, setProjectCnt] = useState<SetupExcpParam>();
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);

  const tabMenus = useMemo(() => {
    return [
      { id: 'LIST', name: t('setup.label.품목 List 조회', '품목 List 조회') },
      { id: 'BARCODE', name: t('setup.label.품목 등록(바코드)', '품목 등록(바코드)') },
      { id: 'NON', name: t('setup.label.품목 등록(수동)', '품목 등록(수동)') },
    ];
  }, []);

  const isEmailFormat = () => {
    if (!userId) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(userId);
  };

  const initSearchParam = {
    bildPlntCds: [],
    elmPrjId: locationParam?.elmPrjId || elmPrjId || '',
    excpId: locationParam?.excpId || (ensolYn === 'Y' ? '' : deptCd),
    excpNm: ensolYn === 'Y' ? '' : deptNm,
    procChkCd: locationParam?.procChkCd || '',
    pakItemStatCd: locationParam?.pakItemStatCd || '',
    pakItemStatCds: locationParam?.pakItemStatCds || [],
    searchOwnedPrjYn: isEmailFormat() ? 'Y' : 'N',
  };

  const initDefaultSearchParam = {
    bildPlntCds: [],
    elmPrjId: '',
    excpId: hasRole('FA_DOVI') || !isEmailFormat() ? '' : deptCd,
    excpNm: hasRole('FA_DOVI') || !isEmailFormat() ? '' : deptNm,
    procChkCd: '',
    procChkGrCd: '',
    pakItemStatCd: '',
    pakItemStatCds: [],
    searchOwnedPrjYn: isEmailFormat() ? 'Y' : 'N',
  };
  const [excpParam, setExcpParam] = useState<SetupExcpParam>({ ...initSearchParam });

  const optionsPrjMaster = useMemo(() => {
    return (prjMasterData || [])
      .filter((o) =>
        (excpParam?.bildPlntCds || []).length > 0
          ? excpParam.bildPlntCds.includes(o.bildPlntCd)
          : true
      )
      .reduce(
        (acc, cur) => [
          ...acc,
          {
            cmnCd: cur.elmPrjId,
            cmnCdNm: cur.elmPrjNm,
          } as Code,
        ],
        [] as Code[]
      );
  }, [prjMasterData, excpParam?.bildPlntCds]);

  const optionsPrjExcp = useMemo(() => {
    return (prjExcpData || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.excpId,
          cmnCdNm: cur.excpNm,
        } as Code,
      ],
      [] as Code[]
    );
  }, [prjExcpData]);

  useEffect(() => {
    if (location.pathname === '/setup/excp/item') {
      setLocationParam(locationState.state);
    }
  }, [locationState.key]);

  useEffect(() => {
    if (!_.isEmpty(locationParam)) {
      setExcpParam((prev) => ({
        ...prev,
        elmPrjId: locationParam?.elmPrjId,
        excpId: locationParam?.excpId,
        pakItemStatCd: locationParam?.pakItemStatCd || '',
        pakItemStatCds: locationParam?.pakItemStatCds || [],
        procChkCd: locationParam?.procChkCd,
      }));
    }
    setLocationStatChange(locationStatChange ? false : true);
  }, [locationParam]);

  useEffect(() => {
    if (excpParam?.elmPrjId) {
      findExcpByElmPrjId(excpParam?.elmPrjId).then((result: SetupSchedule[]) => {
        setPrjExcpData(result);
      });
    } else {
      setPrjExcpData([]);
    }

    if (excpParam?.elmPrjId) {
      btnSearch();
    }
  }, [excpParam?.elmPrjId, locationStatChange]);

  useEffect(() => {
    initCodes();
  }, []);

  const initCodes = async () => {
    const bildPlntOption: Code[] = await getCommonCodeNames('FA_SU_PLNT_CD'); // 플랜트코드
    const procCheckOption: Code[] = await getCommonCodeNames('PRJ_ALM_TP_CD');
    const pakItemStatOption: Code[] = await getCommonCodeNames('ITEM_STATUS');
    setBildPlntCode(bildPlntOption);
    setPakItemStatCode(pakItemStatOption);

    // item 진척점검코드만 가져오기
    setProcCheckCode(() => {
      return procCheckOption.filter((o) => o.optValCtn5 === 'ITEM').map((o) => o);
    });

    // 검색조건의 프로젝트 목록 조회
    findDisplayProject().then((result) => {
      if (result !== null) {
        setPrjMasterData(result);

        if (!excpParam?.elmPrjId && result?.length && result[0]?.elmPrjId) {
          handleOnChange('elmPrjId', result[0].elmPrjId);
          return;
        }
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const btnSearch = () => {
    setErrors({
      elmPrjId: _.isEmpty(excpParam.elmPrjId),
    });

    if (_.isEmpty(excpParam.elmPrjId)) {
      return;
    }

    openLoading(true);
    getItemCnt();
    getSetupItemList(excpParam)
      .then((result: ItemList[]) => {
        if (result !== null) {
          setRowData(result);
        } else {
          openMessageBar({
            type: 'error',
            content: t(
              'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
              '요청 정보 조회 중 오류가 발생했습니다.'
            ),
          });
        }
      })
      .finally(() => openLoading(false));
  };

  const btnReload = () => {
    setExcpParam(initDefaultSearchParam);
  };

  const handleOnChange = (name, value) => {
    setExcpParam((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  };

  const getItemCnt = async () => {
    getProjectItemCnt(excpParam).then((result: ItemList) => {
      if (result) {
        setProjectCnt(result);
      }
    });
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{t('setup.label.플랜트', '플랜트')}</label>
                <MultiComboBox
                  id="bildPlntCd"
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={bildPlntCode || []}
                  defaultValue={excpParam.bildPlntCds || []}
                  onChange={(value) => {
                    handleOnChange('bildPlntCds', value);
                    if (
                      !(prjMasterData || []).filter(
                        (o) =>
                          excpParam?.elmPrjId === o.elmPrjId &&
                          ((value || []).length < 1 || (value || []).includes(o.bildPlntCd || ''))
                      ).length
                    ) {
                      handleOnChange('elmPrjId', '');
                      if (!isEmailFormat()) handleOnChange('excpId', '');
                    }
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <span className={'dot'}>{t('setup.label.프로젝트명', '프로젝트명')}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={optionsPrjMaster}
                  defaultValue={excpParam?.elmPrjId}
                  onChange={(value) => {
                    handleOnChange('elmPrjId', value);
                    if (!isEmailFormat()) handleOnChange('excpId', '');
                  }}
                  isError={errors?.elmPrjId}
                  msgError={String(
                    t('setup.label.프로젝트명을 선택해 주세요.', '프로젝트명을 선택해 주세요.')
                  )}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.협력업체명', '협력업체명')}</label>
                {hasRole('FA_DOVI') || !isEmailFormat() ? (
                  <ComboBox
                    placeholder={String(t('com.label.전체', '전체'))}
                    options={optionsPrjExcp}
                    defaultValue={excpParam?.excpId}
                    onChange={(value) => {
                      handleOnChange('excpId', value);
                    }}
                  />
                ) : (
                  <div className="txt">
                    <span>{excpParam?.excpNm}</span>
                  </div>
                )}
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.진척점검', '진척점검')}</label>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={procCheckCode}
                  defaultValue={excpParam?.procChkCd}
                  onChange={(value) => {
                    handleOnChange('procChkCd', value);
                  }}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{t('setup.label.M-CODE', 'M-CODE')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="eqpId"
                  placeholder={String(
                    t('setup.label.M-CODE를 입력해 주세요.', 'M-CODE를 입력해 주세요.')
                  )}
                  value={excpParam?.eqpId}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.설비명', '설비명')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="tgtEqpNm"
                  placeholder={String(
                    t('setup.label.설비명을 입력해 주세요.', '설비명을 입력해 주세요.')
                  )}
                  value={excpParam?.tgtEqpNm}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.품목명', '품목명')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="pakItemNm"
                  placeholder={String(
                    t('setup.label.품목명을 입력해 주세요.', '품목명을 입력해 주세요.')
                  )}
                  value={excpParam?.pakItemNm}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.바코드 발행여부', '바코드 발행여부')}</label>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={commonYNList}
                  defaultValue={excpParam?.lblIssuYn}
                  onChange={(value) => {
                    handleOnChange('lblIssuYn', value);
                  }}
                  readOnly={currentTabIndex !== 0}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{t('setup.label.상태', '상태')}</label>
                <MultiComboBox
                  id="bildPlntCd"
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={pakItemStatCode || []}
                  defaultValue={excpParam.pakItemStatCds || []}
                  onChange={(value) => {
                    handleOnChange('pakItemStatCds', value);
                    if (
                      !(prjMasterData || []).filter(
                        (o) =>
                          excpParam?.elmPrjId === o.elmPrjId &&
                          ((value || []).length < 1 || (value || []).includes(o.bildPlntCd || ''))
                      ).length
                    ) {
                      handleOnChange('pakItemStatCd', '');
                      if (!isEmailFormat()) handleOnChange('excpId', '');
                    }
                  }}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <Button css={IconButton.button} className="reload" disableRipple onClick={btnReload} />
            <Button css={IconButton.button} className="find" disableRipple onClick={btnSearch}>
              {t('com.button.조회', '조회')}
            </Button>
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <div css={tabs.wrap} style={{ marginTop: '24px' }}>
        <Tabs
          value={currentTabIndex}
          onChange={(e, newValue) => {
            setCurrentTabIndex(newValue);
            setExcpParam((prev) => ({
              ...prev,
              lblIssuYn: newValue === 0 ? '' : newValue === 1 ? 'Y' : 'N',
            }));
          }}
          css={tabs.tab}
        >
          {isEmailFormat() ? (
            tabMenus.map((o, idx) => (
              <Tab
                key={o.id}
                label={o.name}
                id={`setup-item-tab-${idx}`}
                aria-controls={`setup-item-tabpanel-${idx}`}
              />
            ))
          ) : (
            <Tab
              key={tabMenus[0].id}
              label={tabMenus[0].name}
              id={`setup-item-tab-0-LGES`}
              aria-controls={`setup-item-tabpanel-0-LGES`}
            />
          )}
        </Tabs>
      </div>
      <TabPanel value={currentTabIndex} index={currentTabIndex}>
        <TabPanel value={currentTabIndex} index={currentTabIndex}>
          {isEmailFormat() ? (
            tabMenus[currentTabIndex] && (
              <SetupItemList
                id={tabMenus[currentTabIndex].id}
                rowData={rowData}
                itemCnt={projectCnt}
                onSearch={btnSearch}
                excpParam={excpParam}
              />
            )
          ) : (
            <SetupItemList
              id={tabMenus[0].id}
              rowData={rowData}
              itemCnt={projectCnt}
              onSearch={btnSearch}
              excpParam={excpParam}
            />
          )}
        </TabPanel>
      </TabPanel>
    </>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`item-tabpanel-${index}`}>
      {value === index && <div>{children}</div>}
    </div>
  );
}

export default SetupItemManagementPage;
